// pages/overlay/TitleCardEnd.jsx
// Closing end card for the vertical agency-pitch reel. Brand sign-off:
// JOP master logo (hero position) + performer credit + tagline + URL.
// Quieter than the hero card by design — this is the reel's closing moment.
//
//   /overlay/titlecard-end          — preview, auto-loops
//   /overlay/titlecard-end?render   — frame-by-frame WebM download
//   ?bg=green                       — chroma-key background mode
//
// Animation arc (5s):
//   0.00–0.50s  JOP logo fades in (slight upward translate)
//   0.15–0.65s  performer credit fades in
//   0.30–0.75s  tagline fades in
//   0.45–0.85s  URL fades in
//   0.85–5.0s   held, very subtle JOP logo breath

import { useEffect, useRef, useState } from "react";

const W = 1080;
const H = 1920;
const FPS = 60;
const TOTAL_MS = 5000;
const TOTAL_FRAMES = Math.ceil((TOTAL_MS / 1000) * FPS);

// Brand palette
const NAVY   = "#1d52a0";
const ORANGE = "#f57600";
const GOLD   = "#fcb31f";

// Layout
const LOGO_W       = 940;
const LOGO_CY      = 560;     // logo vertical center
const PERFORMER_Y  = 920;
const TAGLINE_Y    = 1080;
const URL_Y        = 1240;

// Timing (ms)
const LOGO_START      = 0;
const LOGO_DUR        = 500;
const PERFORMER_START = 150;
const PERFORMER_DUR   = 500;
const TAGLINE_START   = 300;
const TAGLINE_DUR     = 450;
const URL_START       = 450;
const URL_DUR         = 400;
const HOLD_START      = 850;

// ── EASING ───────────────────────────────────────────────────────────────────
const clamp01 = (t) => Math.max(0, Math.min(1, t));
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

// ── JOP LOGO ─────────────────────────────────────────────────────────────────
function drawJoPLogo(ctx, jopImg, alpha, offsetY, breath) {
  if (alpha <= 0 || !jopImg) return;
  const logoH = LOGO_W * (jopImg.height / jopImg.width);
  const cx = W / 2;
  const cy = LOGO_CY + offsetY;

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(cx, cy);
  ctx.scale(breath, breath);
  ctx.translate(-cx, -cy);

  // Soft drop shadow for separation against any underlying footage
  ctx.shadowColor = "rgba(0, 0, 0, 0.35)";
  ctx.shadowBlur = 28;
  ctx.shadowOffsetY = 12;
  ctx.drawImage(jopImg, cx - LOGO_W / 2, cy - logoH / 2, LOGO_W, logoH);

  ctx.restore();
}

// ── PERFORMER CREDIT ─────────────────────────────────────────────────────────
// "Dr. Dan the Pancake Man" — single confident line, sized so it sits cleanly
// inside the frame with comfortable margins. The hero's two-line Dr./Dan
// hierarchy already happened; here it's just one closing identifier.
function drawPerformer(ctx, alpha, offsetY) {
  if (alpha <= 0) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(0, offsetY);
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.font = "400 52px 'Chango', serif";

  ctx.lineJoin = "round";
  ctx.lineWidth = 7;
  ctx.strokeStyle = "rgba(255,255,255,0.95)";
  ctx.strokeText("Dr. Dan the Pancake Man", W / 2, PERFORMER_Y);
  ctx.fillStyle = NAVY;
  ctx.fillText("Dr. Dan the Pancake Man", W / 2, PERFORMER_Y);

  ctx.restore();
}

// ── TAGLINE ──────────────────────────────────────────────────────────────────
// Lobster Two italic with orange bullet — same script voice as the hero card.
function drawTagline(ctx, alpha, offsetY) {
  if (alpha <= 0) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(0, offsetY);
  ctx.textBaseline = "alphabetic";
  ctx.font = "italic 700 40px 'Lobster Two', cursive";

  const partA = "The original live pancake artist";
  const partB = "·";
  const partC = "Since 2009";
  ctx.textAlign = "left";
  const gap = ctx.measureText("  ").width;
  const wA = ctx.measureText(partA).width;
  const wB = ctx.measureText(partB).width;
  const wC = ctx.measureText(partC).width;
  const totalW = wA + gap + wB + gap + wC;
  const x0 = W / 2 - totalW / 2;
  const xB = x0 + wA + gap;
  const xC = xB + wB + gap;

  ctx.lineJoin = "round";
  ctx.lineWidth = 7;
  ctx.strokeStyle = "rgba(255,255,255,0.95)";
  ctx.strokeText(partA, x0, TAGLINE_Y);
  ctx.strokeText(partB, xB, TAGLINE_Y);
  ctx.strokeText(partC, xC, TAGLINE_Y);

  ctx.fillStyle = NAVY;
  ctx.fillText(partA, x0, TAGLINE_Y);
  ctx.fillStyle = ORANGE;
  ctx.fillText(partB, xB, TAGLINE_Y);
  ctx.fillStyle = NAVY;
  ctx.fillText(partC, xC, TAGLINE_Y);

  ctx.restore();
}

// ── URL ──────────────────────────────────────────────────────────────────────
function drawURL(ctx, alpha, offsetY) {
  if (alpha <= 0) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(0, offsetY);
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.font = "600 42px Fredoka, sans-serif";
  ctx.letterSpacing = "1px";

  ctx.lineJoin = "round";
  ctx.lineWidth = 7;
  ctx.strokeStyle = "rgba(255,255,255,0.95)";
  ctx.strokeText("thejoyofpancakes.com", W / 2, URL_Y);
  ctx.fillStyle = NAVY;
  ctx.fillText("thejoyofpancakes.com", W / 2, URL_Y);

  ctx.letterSpacing = "0px";
  ctx.restore();
}

// ── FRAME DRAWING ────────────────────────────────────────────────────────────
function drawFrame(ctx, frameNum, bgMode, jopImg) {
  const ms = (frameNum / FPS) * 1000;

  ctx.clearRect(0, 0, W, H);
  if (bgMode === "green") {
    ctx.fillStyle = "#00FF00";
    ctx.fillRect(0, 0, W, H);
  }

  // JOP logo — fade in, tiny upward drift, very subtle breath during hold
  const logoP = clamp01((ms - LOGO_START) / LOGO_DUR);
  const logoEased = easeOutCubic(logoP);
  const logoOffsetY = (1 - logoEased) * -18; // settles down from 18px above
  let breath = 1;
  if (ms > HOLD_START) {
    const t = (ms - HOLD_START) / 1000;
    breath = 1 + Math.sin(t * Math.PI * 0.35) * 0.004;
  }
  drawJoPLogo(ctx, jopImg, logoEased, logoOffsetY, breath);

  // Performer credit
  const perfP = clamp01((ms - PERFORMER_START) / PERFORMER_DUR);
  if (perfP > 0) {
    const eased = easeOutCubic(perfP);
    drawPerformer(ctx, eased, (1 - eased) * 14);
  }

  // Tagline
  const tagP = clamp01((ms - TAGLINE_START) / TAGLINE_DUR);
  if (tagP > 0) {
    const eased = easeOutCubic(tagP);
    drawTagline(ctx, eased, (1 - eased) * 12);
  }

  // URL
  const urlP = clamp01((ms - URL_START) / URL_DUR);
  if (urlP > 0) {
    const eased = easeOutCubic(urlP);
    drawURL(ctx, eased, (1 - eased) * 10);
  }
}

// ── ASSET LOADING ────────────────────────────────────────────────────────────
async function ensureAssetsLoaded() {
  if (document.fonts) {
    await Promise.all([
      document.fonts.load("400 52px 'Chango'"),
      document.fonts.load("italic 700 40px 'Lobster Two'"),
      document.fonts.load("600 42px Fredoka"),
    ]);
  }
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = "/jop_logo_wide.png";
  });
  return { jopImg: img };
}

// ── PREVIEW MODE ─────────────────────────────────────────────────────────────
function PreviewMode({ bgMode }) {
  const canvasRef = useRef(null);
  const [assets, setAssets] = useState(null);

  useEffect(() => { ensureAssetsLoaded().then(setAssets); }, []);

  useEffect(() => {
    if (!assets) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let frame = 0;
    let raf;
    function tick() {
      drawFrame(ctx, frame, bgMode, assets.jopImg);
      frame++;
      if (frame > TOTAL_FRAMES + 60) frame = 0;
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [assets, bgMode]);

  return (
    <div style={{ width: "100vw", height: "100vh", background: "#1a1a1a", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
      <canvas ref={canvasRef} width={W} height={H} style={{ maxWidth: "100%", maxHeight: "100%", boxShadow: "0 8px 40px rgba(0,0,0,0.5)" }} />
      <div style={{ position: "absolute", bottom: "1rem", left: "50%", transform: "translateX(-50%)", color: "rgba(255,255,255,0.55)", fontSize: "0.85rem", fontFamily: "system-ui", textAlign: "center", lineHeight: 1.5 }}>
        End Card · bg={bgMode} · auto-loops<br />
        <span style={{ opacity: 0.7 }}>?render to download · ?bg=green for chroma-key</span>
      </div>
    </div>
  );
}

// ── RENDER MODE ──────────────────────────────────────────────────────────────
function RenderMode({ bgMode }) {
  const [status, setStatus] = useState("Loading assets...");
  const canvasRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const assets = await ensureAssetsLoaded();
      if (cancelled) return;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      setStatus("Rendering frames...");
      const stream = canvas.captureStream(0);
      const recorder = new MediaRecorder(stream, {
        mimeType: "video/webm;codecs=vp9",
        videoBitsPerSecond: 12_000_000,
      });
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.start();

      for (let f = 0; f <= TOTAL_FRAMES + 5; f++) {
        drawFrame(ctx, f, bgMode, assets.jopImg);
        const track = stream.getVideoTracks()[0];
        if (track.requestFrame) track.requestFrame();
        await new Promise((r) => setTimeout(r, 1000 / FPS));
        if (f % 30 === 0) setStatus(`Rendering frame ${f}/${TOTAL_FRAMES}...`);
      }

      ctx.clearRect(0, 0, W, H);
      const track = stream.getVideoTracks()[0];
      if (track.requestFrame) track.requestFrame();
      await new Promise((r) => setTimeout(r, 100));

      recorder.stop();
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: "video/webm" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `end-card-${bgMode}.webm`;
        a.click();
        setStatus(`Downloaded end-card-${bgMode}.webm`);
      };
    })();
    return () => { cancelled = true; };
  }, [bgMode]);

  return (
    <div style={{ width: "100vw", height: "100vh", background: "#0d1833", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "1.5rem", fontFamily: "system-ui" }}>
      <canvas ref={canvasRef} width={W} height={H} style={{ maxHeight: "75vh", maxWidth: "90vw", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8 }} />
      <div style={{ color: GOLD, fontSize: "1.1rem", fontWeight: 700 }}>{status}</div>
    </div>
  );
}

// ── ROUTER ───────────────────────────────────────────────────────────────────
export default function TitleCardEnd() {
  const params = new URLSearchParams(window.location.search);
  const isRender = params.has("render");
  const bgMode = params.get("bg") === "green" ? "green" : "transparent";
  return isRender ? <RenderMode bgMode={bgMode} /> : <PreviewMode bgMode={bgMode} />;
}
